import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Users, Check } from "lucide-react";
import React from "react";
import { UserInterface } from "../../../../models/UserModel/User";
import { useLanguageStore } from "../../../../store/useLanguageStore";

interface TeamMemberFilterProps {
  teamMembers: UserInterface[];
  selectedMembers: string[];
  onSelectedMembersChange: (members: string[] | ((prev: string[]) => string[])) => void;
  darkMode: boolean;
}

const avatarColors = [
  "bg-blue-500 text-white",
  "bg-purple-500 text-white",
  "bg-green-500 text-white",
  "bg-orange-500 text-white",
  "bg-pink-500 text-white",
  "bg-indigo-500 text-white",
];

export const TeamMemberFilter = React.memo(function TeamMemberFilter({
  teamMembers,
  selectedMembers,
  onSelectedMembersChange,
  darkMode,
}: TeamMemberFilterProps) {
  const { language } = useLanguageStore();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allSelected = useMemo(
    () => selectedMembers.length === teamMembers.length && teamMembers.length > 0,
    [selectedMembers.length, teamMembers.length]
  );

  const toggleMember = useCallback(
    (memberId: string) => {
      onSelectedMembersChange((prev) => {
        if (prev.includes(memberId)) return prev.filter((id) => id !== memberId);
        return [...prev, memberId];
      });
    },
    [onSelectedMembersChange]
  );

  const selectAll = useCallback(() => {
    onSelectedMembersChange(teamMembers.map((m) => m.id || "").filter(Boolean));
  }, [teamMembers, onSelectedMembersChange]);

  const deselectAll = useCallback(() => {
    onSelectedMembersChange([]);
  }, [onSelectedMembersChange]);

  const getInitials = (firstname: string, lastname: string) => `${firstname[0]}${lastname[0]}`.toUpperCase();

  const getAvatarColor = (index: number) => avatarColors[index % avatarColors.length];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`h-8 px-3 gap-2 inline-flex items-center justify-center rounded-md text-sm border transition-colors
    ${
      darkMode
        ? "bg-[#2B2C40] text-white border-white/10 hover:bg-white/10"
        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
    }
  `}
      >
        <Users className="w-4 h-4" />

        {language["members"]}

        <span
          className={`ml-1 px-1.5 py-0.5 rounded text-xs
      ${darkMode ? "bg-white/15 text-white" : "bg-blue-100 text-blue-700"}
    `}
        >
          {selectedMembers.length}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              {language["filterByMembers"]}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {teamMembers.length} {language["staffTotal"]}
            </p>
          </div>
          <div className="py-1 max-h-80 overflow-y-auto">
            <button
              onClick={allSelected ? deselectAll : selectAll}
              className="w-full px-3 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100"
            >
              <div className="w-4 h-4 rounded border border-blue-400 flex items-center justify-center bg-blue-50 flex-shrink-0">
                {allSelected && <Check className="w-3 h-3 text-blue-600" />}
              </div>
              <span className="text-xs text-blue-600 font-medium flex-1">
                {allSelected ? `${language["removeAll"]}` : `${language["selectAll"]}`}
              </span>
            </button>

            {teamMembers.map((member, index) => {
              const isSelected = selectedMembers.includes(member.id || "");
              const fullName = `${member.firstname} ${member.lastname}`;

              return (
                <button
                  key={member.id}
                  onClick={() => toggleMember(member.id || "")}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-4 h-4 rounded border border-gray-300 flex items-center justify-center bg-white flex-shrink-0">
                    {isSelected && <Check className="w-3 h-3 text-blue-600" />}
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full ${getAvatarColor(
                      index
                    )} flex items-center justify-center text-white text-[9px] font-medium flex-shrink-0`}
                  >
                    {getInitials(member.firstname, member.lastname)}
                  </div>
                  <span className={`text-xs flex-1 ${darkMode ? "text-white" : "text-gray-700"}`}>{fullName}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});
